import { useEffect, useState } from 'react'
import axios from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'

const emptyLocales: string[] = []

type UseMarkdownContentResult = {
  markdown: string
  error: string | null
  loading: boolean
  locales: string[]
  selectedLocale: string | null
  setSelectedLocale: (locale: string) => void
}

export default function useMarkdownContent(
  baseFilename: string,
  directoryUrl: string,
  contentBaseUrl: string
): UseMarkdownContentResult {
  const [markdown, setMarkdown] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [locales, setLocales] = useState(emptyLocales)
  const [selectedLocale, setSelectedLocale] = useState<string | null>(null)

  useEffect(() => {
    async function fetchAvailableLocales() {
      try {
        const response = await axios.get<{ locales?: string[] }>(directoryUrl)
        const availableLocales = response.data.locales || []
        setLocales(availableLocales)

        const userLanguage = i18n.language
        let initialLocale = userLanguage
        if (!availableLocales.includes(userLanguage)) {
          initialLocale = availableLocales.includes('en') ? 'en' : availableLocales[0]
        }
        setSelectedLocale(initialLocale)
      } catch (fetchError) {
        console.error('Error fetching locales:', fetchError)
        setLocales(['en'])
        setSelectedLocale('en')
      }
    }

    fetchAvailableLocales()
  }, [directoryUrl])

  useEffect(() => {
    if (locales.length > 0) {
      const userLanguage = i18n.language
      let newLocale = userLanguage
      if (!locales.includes(userLanguage)) {
        newLocale = locales.includes('en') ? 'en' : locales[0]
      }
      setSelectedLocale(newLocale)
    }
  }, [locales, baseFilename])

  useEffect(() => {
    async function fetchMarkdown() {
      if (!baseFilename) {
        setLoading(false)
        return
      }

      if (!selectedLocale) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        const url = `${contentBaseUrl}/${selectedLocale}/${baseFilename}.md`
        const response = await axios.get<string>(url)
        setMarkdown(response.data)
        setLoading(false)
      } catch {
        if (selectedLocale !== 'en') {
          try {
            const fallbackUrl = `${contentBaseUrl}/en/${baseFilename}.md`
            const fallbackResponse = await axios.get<string>(fallbackUrl)
            setMarkdown(fallbackResponse.data)
            setLoading(false)
            return
          } catch (fallbackError) {
            console.error('Error loading markdown (including English fallback):', fallbackError)
            setError('Error loading content.')
            setMarkdown('')
            setLoading(false)
            return
          }
        }

        setError('Error loading content.')
        setMarkdown('')
        setLoading(false)
      }
    }

    fetchMarkdown()
  }, [selectedLocale, baseFilename, contentBaseUrl])

  return {
    markdown,
    error,
    loading,
    locales,
    selectedLocale,
    setSelectedLocale,
  }
}
